import dayjs from 'dayjs'
import type { ApplicantTaskDuePreset } from 'types/graphql'

import type { TaskStatus } from 'src/graphql/types/tasks'
import { formatSnakeValue } from 'src/lib/formatters'
import { formatLeadSourceFilterValue } from 'src/lib/leadSource.utils'
import { OPEN_TASK_STATUSES } from 'src/lib/taskStatus'

import {
  ANY_ACTIVE_TASK,
  ANY_ASSIGNEE,
} from '../components/FilterMenu/utils/taskFilterOptions'
import {
  MultiSelectFilter,
  RangeFilter,
  DateRangeFilter,
  SearchFilter,
  Filters,
  StepFilters,
} from '../components/FiltersSiderbar/Filters'
import { SelectedFilter } from '../components/SelectedFiltersBar/SelectedFiltersBar'

const DATE_FORMAT = 'MM/DD/YYYY'

interface FilterParams {
  applicantStages: string[]
  searchText: string
  applicationDate: { gte: Date | null; lte: Date | null }
  lastContactDate: { gte: Date | null; lte: Date | null }
  availableForWorkDate: { gte: Date | null; lte: Date | null }
  hiredDate: { gte: Date | null; lte: Date | null }
  terminatedDate: { gte: Date | null; lte: Date | null }
  disqualifiedDate: { gte: Date | null; lte: Date | null }
  notInterestedDate: { gte: Date | null; lte: Date | null }
  bookingDate: { gte: Date | null; lte: Date | null }
  recruiters: string[]
  jobListings: string[]
  leadSources: string[]
  endorsements: string[]
  states: string[]
  tags: string[]
  equipmentTypes: string[]
  zipCodes: string[]
  missedOutboundCallCount: { gte: number | null; lte: number | null }
  eligibleForRehire?: boolean
  isPermanentlyDisqualified?: boolean
  customFields?: any[]
  tasks?: {
    taskStatuses?: TaskStatus[]
    duePresets?: ApplicantTaskDuePreset[]
    taskTemplateIds?: string[]
    taskAssigneeIds?: string[]
    assigned?: boolean
    dueDateRange?: { from?: string; to?: string }
  }
}

export function buildFiltersGraphqlParam(filters: Filters): FilterParams {
  if (!filters) {
    return
  }
  const filterGraphqlParam: FilterParams = {
    applicantStages: [],
    searchText: '',
    applicationDate: { gte: null, lte: null },
    lastContactDate: { gte: null, lte: null },
    availableForWorkDate: { gte: null, lte: null },
    hiredDate: { gte: null, lte: null },
    terminatedDate: { gte: null, lte: null },
    disqualifiedDate: { gte: null, lte: null },
    notInterestedDate: { gte: null, lte: null },
    bookingDate: { gte: null, lte: null },
    recruiters: [],
    jobListings: [],
    leadSources: [],
    endorsements: [],
    states: [],
    tags: [],
    equipmentTypes: [],
    zipCodes: [],
    missedOutboundCallCount: { gte: null, lte: null },
  }
  const keys = Object.keys(filters)
  for (const key of keys) {
    const filter = filters[key]
    if (filter.type === 'multi-select') {
      const selectedValues = (filter as MultiSelectFilter).selectedValues || []
      // The Eligible for Hire / Permanently Disqualified filters are modelled
      // as Yes/No multi-selects for UI reuse, but map to scalar booleans in the
      // GraphQL contract. Convert here and omit the field entirely when nothing
      // is selected so no clause is added.
      if (filter.key === 'eligibleForHire') {
        if (selectedValues.length > 0) {
          filterGraphqlParam.eligibleForRehire =
            selectedValues[selectedValues.length - 1] === 'true'
        }
      } else if (filter.key === 'permanentlyDisqualified') {
        if (selectedValues.length > 0) {
          filterGraphqlParam.isPermanentlyDisqualified =
            selectedValues[selectedValues.length - 1] === 'true'
        }
      } else if (filter.key === 'taskDeadline') {
        if (selectedValues.length) {
          const tasks = (filterGraphqlParam.tasks ||= {})
          // "Any deadline" is a superset of every window, so it drops the presets
          const presets = selectedValues.filter(
            (v): v is ApplicantTaskDuePreset => v !== ANY_ACTIVE_TASK
          )
          if (presets.length && !selectedValues.includes(ANY_ACTIVE_TASK)) {
            tasks.duePresets = presets
          }
          tasks.taskStatuses = OPEN_TASK_STATUSES
        }
      } else if (filter.key === 'taskTemplates') {
        if (selectedValues.length) {
          const tasks = (filterGraphqlParam.tasks ||= {})
          tasks.taskTemplateIds = selectedValues
        }
      } else if (filter.key === 'taskAssignees') {
        if (selectedValues.length) {
          const tasks = (filterGraphqlParam.tasks ||= {})
          // "Any assignee" is a superset of every employee, so it drops the ids
          if (selectedValues.includes(ANY_ASSIGNEE)) {
            tasks.assigned = true
          } else {
            tasks.taskAssigneeIds = selectedValues
          }
        }
      } else {
        filterGraphqlParam[filter.key] = selectedValues
      }
    }
    if (filter.type === 'range' || filter.type === 'date-range') {
      const range = {
        lte: (filter as RangeFilter | DateRangeFilter).lte,
        gte: (filter as RangeFilter | DateRangeFilter).gte,
      }
      if (filter.key === 'taskDueDate') {
        if (range.gte || range.lte) {
          const tasks = (filterGraphqlParam.tasks ||= {})
          // Calendar days; the API resolves them in the company timezone
          tasks.dueDateRange = {
            ...(range.gte && { from: dayjs(range.gte).format('YYYY-MM-DD') }),
            ...(range.lte && { to: dayjs(range.lte).format('YYYY-MM-DD') }),
          }
        }
      } else {
        filterGraphqlParam[filter.key] = range
      }
    }
    if (filter.type === 'search') {
      filterGraphqlParam[filter.key] = (filter as SearchFilter).searchText
    }
    if (filter.type === 'step-filter') {
      filterGraphqlParam[filter.key] = {
        OR: (filter as StepFilters).selectedValues || [],
      }
    }
    if (filter.type === 'applicant-stage-filter') {
      filterGraphqlParam[filter.key] = {
        OR: (filter as { selectedValues?: unknown[] }).selectedValues || [],
      }
    }
    if (filter.type === 'location') {
      filterGraphqlParam[filter.key] = filter.values || []
    }
  }

  // Add custom fields if they exist
  if (filters.customFields) {
    filterGraphqlParam.customFields = filters.customFields
  }

  // Assignee and template selections imply open tasks unless statuses were set
  const tasks = filterGraphqlParam.tasks
  if (tasks && !tasks.taskStatuses?.length) {
    const constrained =
      tasks.duePresets?.length ||
      tasks.dueDateRange ||
      tasks.taskAssigneeIds?.length ||
      tasks.assigned ||
      tasks.taskTemplateIds?.length
    if (constrained) {
      tasks.taskStatuses = OPEN_TASK_STATUSES
    }
  }

  return filterGraphqlParam
}

export function buildSelectedFilters(
  selectedValuesByFilter,
  customFields = [],
  customStepLabels: Record<string, string> = {}
): SelectedFilter[] {
  const selectedFilters: SelectedFilter[] = []
  for (const key in selectedValuesByFilter) {
    const filter = selectedValuesByFilter[key]

    if (filter?.type === 'multi-select' && filter?.selectedValues?.length > 0) {
      // Lead Source values are nested-encoded ("TYPE" / "TYPE:name" /
      // "TYPE:__NO_NAME__") and its options load asynchronously, so they can't
      // be resolved against top-level options[].value. Derive the chip label
      // from the encoded value itself so it reads e.g. "Recruiter: Allison".
      if (filter.key === 'leadSources') {
        filter.selectedValues.forEach((selectedValue) => {
          selectedFilters.push({
            key: filter.key,
            label: formatLeadSourceFilterValue(selectedValue),
            value: selectedValue,
          })
        })
        continue
      }

      // For dynamic filters (jobListings, recruiters, tags), only show selected filters
      // when we can find matching options to avoid showing IDs instead of labels
      const isDynamicFilter =
        filter.key === 'jobListings' ||
        filter.key === 'recruiters' ||
        filter.key === 'tags' ||
        filter.key === 'taskAssignees' ||
        filter.key === 'taskTemplates'

      // For dynamic filters, only show selected filters when options are available
      // to avoid showing IDs instead of labels
      if (isDynamicFilter) {
        // Check if options array exists and has items
        const hasOptions =
          Array.isArray(filter.options) && filter.options.length > 0

        if (!hasOptions) {
          // Options haven't loaded yet, skip for now - will show once options load
          continue
        }

        // For dynamic filters, we need to find matching options
        // Process each selectedValue and only add it if we can find a matching option
        // This ensures we show labels, not IDs
        filter.selectedValues.forEach((selectedValue) => {
          const option = filter.options.find(
            (option) => option?.value === selectedValue
          )

          // Only add the filter if we found a matching option with a label
          if (option && option.label) {
            selectedFilters.push({
              key: filter.key,
              // Disambiguate from the Employees chips
              label:
                filter.key === 'taskAssignees'
                  ? `Assignee: ${option.label}`
                  : option.label,
              value: selectedValue,
            })
          }
          // If no matching option found, skip this selectedValue to avoid showing ID
        })

        // Skip the default processing below for dynamic filters since we handled it above
        continue
      }

      filter.selectedValues.forEach((selectedValue) => {
        const option = filter.options?.find(
          (option) => option?.value === selectedValue
        )
        // If option is found, use its label. Otherwise, use the value as label (for static filters)
        // For dynamic filters, we should have options by now, but fallback to value if not found
        const baseLabel = option?.label || selectedValue
        // Boolean Yes/No filters ("Eligible for Hire", "Permanently
        // Disqualified") need the filter title prefixed so the "Yes"/"No" pill
        // isn't ambiguous.
        const label =
          filter.key === 'eligibleForHire' ||
          filter.key === 'permanentlyDisqualified'
            ? `${filter.title}: ${baseLabel}`
            : baseLabel
        selectedFilters.push({
          key: filter.key,
          label,
          value: selectedValue,
        })
      })
    } else if (
      filter?.type === 'range' &&
      ((filter?.gte !== null && filter?.gte !== undefined) ||
        (filter?.lte !== null && filter?.lte !== undefined))
    ) {
      const hasGte = filter.gte !== null && filter.gte !== undefined
      const hasLte = filter.lte !== null && filter.lte !== undefined

      let label: string
      if (hasGte && hasLte) {
        if (filter.gte === filter.lte) {
          label = `${filter.gte}`
        } else {
          label = `${filter.gte} - ${filter.lte}`
        }
      } else if (hasGte) {
        label = `${filter.gte}+`
      } else if (hasLte) {
        // When only max is set, show range from min to max
        label = `${filter.min} - ${filter.lte}`
      } else {
        label = ''
      }

      const areDifferentFromLimits =
        filter.gte !== filter.min || filter.lte !== filter.max

      if (areDifferentFromLimits) {
        if (filter.key === 'yearsOfExperience') {
          selectedFilters.push({
            key: filter.key,
            label: `experience: ${label} years`,
            value: filter.selectedValues,
          })
        }
        if (filter.key === 'callCount') {
          selectedFilters.push({
            key: filter.key,
            label: `Call count: ${label}`,
            value: filter.selectedValues,
          })
        }
        if (filter.key === 'missedOutboundCallCount') {
          selectedFilters.push({
            key: filter.key,
            label: `Missed call count: ${label}`,
            value: filter.selectedValues,
          })
        }
      }
    } else if (filter?.type === 'search' && filter.searchText) {
      selectedFilters.push({
        key: filter.key,
        label: `search: ${filter.searchText}`,
        value: filter.searchText,
      })
    } else if (filter?.type === 'date-range' && (filter?.gte || filter?.lte)) {
      const existsBothAndAreDifferent =
        typeof filter.gte &&
        typeof filter.lte &&
        filter.gte &&
        filter.lte &&
        filter.gte != filter.lte

      const dateLabel = existsBothAndAreDifferent
        ? `${dayjs(filter.gte).format(DATE_FORMAT)} - ${dayjs(
            filter.lte
          ).format(DATE_FORMAT)}`
        : `${
            dayjs(filter.gte).format(DATE_FORMAT) ||
            dayjs(filter.lte).format(DATE_FORMAT)
          }`

      selectedFilters.push({
        key: filter.key,
        label: `${filter.title || 'Date'}: ${dateLabel}`,
        value: `${filter.gte || ''}-${filter.lte || ''}`,
      })
    } else if (
      filter?.type === 'step-filter' &&
      filter?.selectedValues?.length > 0
    ) {
      filter.selectedValues.forEach((selectedValue) => {
        const statusPart = selectedValue.status
          ? `: ${formatSnakeValue(selectedValue.status)}`
          : ''

        if (selectedValue.customStepId) {
          const label = customStepLabels[selectedValue.customStepId]
          if (!label) return

          selectedFilters.push({
            key: `${filter.key}-${selectedValue.customStepId}-${selectedValue.status}`,
            label: `${label}${statusPart}`,
            value: selectedValue,
          })
          return
        }

        const option = filter.options.find(
          (option) => option.value === selectedValue.type
        )
        if (option) {
          selectedFilters.push({
            key: `${filter.key}-${selectedValue.type}-${selectedValue.status}`,
            label: `${option.label}${statusPart}`,
            value: selectedValue,
          })
        }
      })
    } else if (
      filter?.type === 'applicant-stage-filter' &&
      filter?.selectedValues?.length > 0
    ) {
      filter.selectedValues.forEach((selectedValue) => {
        const option = filter.options.find(
          (option) => option.value === selectedValue.applicantStage
        )
        if (option) {
          // Check if status exists and is not undefined
          const subOption =
            selectedValue.hiringStage ||
            selectedValue.terminatedReason ||
            selectedValue.disqualifiedReason ||
            selectedValue.notInterestedReason
          const statusPart = subOption ? `: ${formatSnakeValue(subOption)}` : ''

          selectedFilters.push({
            key: `${filter.key}-${selectedValue.applicantStage}-${subOption}`,
            label: `${option.label}${statusPart}`,
            value: selectedValue,
          })
        }
      })
    } else if (filter?.type === 'location') {
      if (filter?.values?.length > 0) {
        for (const zipCode of filter.values) {
          selectedFilters.push({
            key: filter.key,
            label: zipCode,
            value: zipCode,
          })
        }
      }
    }
  }

  // Handle custom fields (stored in filters.customFields array)
  if (selectedValuesByFilter.customFields?.length > 0) {
    selectedValuesByFilter.customFields.forEach((cf) => {
      // Find the custom field metadata to get the field name and type
      const fieldMetadata = customFields.find(
        (f) => f.customFieldId === cf.customFieldId
      )
      const fieldName = fieldMetadata?.label || 'Custom Field'

      let displayValue = ''

      if (cf.stringValue !== null && cf.stringValue !== undefined) {
        displayValue = cf.stringValue
      } else if (cf.booleanValue !== null && cf.booleanValue !== undefined) {
        displayValue = cf.booleanValue ? 'Yes' : 'No'
      } else if (cf.dateValue) {
        // Check if date value is actually empty (both gte and lte are null/undefined)
        const hasDateValue =
          (cf.dateValue.gte !== null && cf.dateValue.gte !== undefined) ||
          (cf.dateValue.lte !== null && cf.dateValue.lte !== undefined)

        if (hasDateValue) {
          if (cf.dateValue.gte && cf.dateValue.lte) {
            displayValue = `${dayjs(cf.dateValue.gte).format(
              DATE_FORMAT
            )} - ${dayjs(cf.dateValue.lte).format(DATE_FORMAT)}`
          } else if (cf.dateValue.gte) {
            displayValue = `After ${dayjs(cf.dateValue.gte).format(
              DATE_FORMAT
            )}`
          } else if (cf.dateValue.lte) {
            displayValue = `Before ${dayjs(cf.dateValue.lte).format(
              DATE_FORMAT
            )}`
          }
        }
        // If date value is empty, displayValue remains empty and we skip this filter
      } else if (cf.selectValues && cf.selectValues.length > 0) {
        // For select fields, try to get the option labels
        if (fieldMetadata?.options) {
          displayValue = cf.selectValues
            .map((val) => {
              const option = fieldMetadata.options.find((o) => o.value === val)
              return option?.label || val
            })
            .join(', ')
        } else {
          displayValue = cf.selectValues.join(', ')
        }
      }

      // Only add filter if it has a display value (skip empty filters)
      if (displayValue) {
        selectedFilters.push({
          key: `customField-${cf.customFieldId}`,
          label: `${fieldName}: ${displayValue}`,
          value: cf.customFieldId, // Use customFieldId as value so we can clear it
        })
      }
    })
  }

  return selectedFilters
}
